import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";

export const Route = createFileRoute("/admin/slides")({
  component: AdminSlides,
});

type Slide = {
  id: string;
  image_url: string;
  cta_to: string;
  sort_order: number;
  active: boolean;
};

const empty = {
  image_url: "",
  eyebrow: "",
  title: "Slide",
  body: "",
  cta_label: "Shop",
  cta_to: "/shop",
  sort_order: 0,
  active: true,
};

function AdminSlides() {
  const [items, setItems] = useState<Slide[]>([]);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data, error } = await supabase.from("hero_slides").select("*").order("sort_order");
    if (error) toast.error(error.message);
    else setItems((data as Slide[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("hero-slides").upload(path, file, { upsert: false, cacheControl: "31536000" });
    if (error) {
      toast.error(error.message);
      setUploading(false);
      return;
    }
    const { data } = supabase.storage.from("hero-slides").getPublicUrl(path);
    setForm((f) => ({ ...f, image_url: data.publicUrl }));
    toast.success("Image uploaded");
    setUploading(false);
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.image_url) return toast.error("Please upload an image");
    const { error } = await supabase.from("hero_slides").insert(form);
    if (error) return toast.error(error.message);
    toast.success("Slide added");
    setForm(empty);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this slide?")) return;
    const { error } = await supabase.from("hero_slides").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const toggle = async (s: Slide) => {
    const { error } = await supabase.from("hero_slides").update({ active: !s.active }).eq("id", s.id);
    if (error) return toast.error(error.message);
    load();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="rounded-lg border border-border p-5 space-y-3 bg-card">
        <h2 className="text-lg font-medium">Add slide</h2>
        <p className="text-xs text-muted-foreground">
          Upload a banner image in <strong>YouTube thumbnail size (1280 × 720, 16:9)</strong> for best fit.
        </p>
        <div className="space-y-2">
          <input
            type="file"
            accept="image/*"
            onChange={handleUpload}
            disabled={uploading}
            className="block w-full text-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-foreground file:text-background file:rounded file:cursor-pointer border border-border rounded bg-background"
          />
          {form.image_url && (
            <img
              src={form.image_url}
              alt=""
              className="w-full max-w-md aspect-video object-cover rounded border border-border"
            />
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            placeholder="Link (e.g. /shop)"
            value={form.cta_to}
            onChange={(e) => setForm({ ...form, cta_to: e.target.value })}
            className="border border-border px-3 py-2 rounded text-sm bg-background"
          />
          <input
            type="number"
            placeholder="Sort order"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
            className="border border-border px-3 py-2 rounded text-sm bg-background"
          />
        </div>
        <button className="inline-flex items-center gap-2 bg-foreground text-background px-4 py-2 text-sm rounded">
          <Plus className="h-4 w-4" /> Add slide
        </button>
      </form>

      <div className="rounded-lg border border-border divide-y divide-border bg-card">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">No slides yet.</p>}
        {items.map((s) => (
          <div key={s.id} className="flex items-center gap-4 p-4">
            <img src={s.image_url} alt="" className="aspect-video w-32 object-cover rounded bg-muted" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{s.cta_to || "—"}</p>
              <p className="text-xs text-muted-foreground truncate">order {s.sort_order}</p>
            </div>
            <button onClick={() => toggle(s)} className="text-xs px-2 py-1 border border-border rounded">
              {s.active ? "Active" : "Hidden"}
            </button>
            <button onClick={() => remove(s.id)} className="text-destructive p-2 hover:bg-destructive/10 rounded">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

